import Checkbox from "components/checkbox/Checkbox";
import { getErrorDetail } from "components/utility/utils";
import { useAuthModals } from "contexts/auth-modals-context";
import { usePhonePortraitContext } from "contexts/phone-portrait-context";
import { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Col,
  Form,
  InputGroup,
  Row,
  Spinner,
} from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import completeSignup from "services/client-portal/completeSignup";
import { fetchOrchidAPI } from "utils/api";
import {
  PASSWORD_PATTERN,
  STRICT_PASSWORD_PATTERN,
} from "constants/passwordPattern";
import getClasses from "utils/getClasses";
import styles from "./MemberRegisterForm.module.scss";

type Props = {
  isAdultCheckId: string;
  isInModal?: boolean;
};

const MemberRegisterForm = ({ isAdultCheckId, isInModal }: Props) => {
  const { isPhonePortrait } = usePhonePortraitContext();
  const { setCustomEmailSentModalProps, openEmailSentModal } = useAuthModals();
  const location = useLocation();

  const { completeSignupToken } = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return {
      completeSignupToken: query.get("complete_signup_token") || "",
    };
  }, [location.search]);

  const [state, setState] = useState({
    first_name: "",
    email: "",
    password: "",
    invite_code:
      new URLSearchParams(location.search).get("invite_code") || null,
    patient_chart_invite_code:
      new URLSearchParams(location.search).get("patient_chart_invite_code") ||
      null,
    busy: false,
    revealPassword: false,
    is_over_eighteen: undefined,
    error: "",
    showUnmetPasswordRequirement: false,
    validated: false,
  });

  const handleSubmit = async (event) => {
    event.preventDefault();

    window.localStorage.setItem(
      "redirect_after_register",
      window.location.pathname + window.location.search + window.location.hash,
    );
    console.log(window.localStorage.getItem("redirect_after_register"));

    const form = event.currentTarget;

    if (!form.checkValidity()) {
      setState({
        ...state,
        validated: true,
        error: "Please fill out all of the required fields correctly",
        showUnmetPasswordRequirement: true,
      });
      return;
    }

    const password = form.querySelector("#password");
    const passwordValidated = STRICT_PASSWORD_PATTERN.test(password.value);

    if (!passwordValidated) {
      setState({
        ...state,
        validated: true,
        error:
          "Password must be at least 10 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character.",
        showUnmetPasswordRequirement: true,
      });
      return;
    }

    setState({ ...state, busy: true });

    if (completeSignupToken) {
      try {
        await completeSignup(completeSignupToken, state.password);
        setCustomEmailSentModalProps({
          email: state.email,
        });
        openEmailSentModal();
      } catch (error: any) {
        setState({ ...state, error: error });
      }
    } else {
      try {
        const response = await fetchOrchidAPI("/api/users/v1/patient/account", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_in: {
              email: state.email,
              password: state.password,
              invite_code: state.invite_code,
              patient_chart_invite_code: state.patient_chart_invite_code,
            },
            patient_in: {
              first_name: state.first_name,
              is_over_eighteen: state.is_over_eighteen,
            },
          }),
        });
        if (!response.ok) {
          setState({
            ...state,
            error: `Failed to register: ${getErrorDetail(
              await response.text(),
            )}`,
            busy: false,
          });
          return;
        }
        setCustomEmailSentModalProps({
          email: state.email,
        });
        openEmailSentModal();
      } catch (err) {
        console.error(err);
      }
    }
  };

  const onInputChange = (event) => {
    setState({
      ...state,
      [event.target.id]: event.target.value,
    });
  };

  const onAdultChecked = (event) => {
    setState({ ...state, is_over_eighteen: event.currentTarget.checked });
  };

  const togglePassword = (event) => {
    event.preventDefault();
    setState({ ...state, revealPassword: !state.revealPassword });
  };

  return (
    <Form
      onSubmit={handleSubmit}
      validated={state.validated}
      className={getClasses(isPhonePortrait && styles["auth-form--phone"])}
      noValidate
    >
      {completeSignupToken ? null : !state.patient_chart_invite_code ? (
        <Row>
          <Form.Group as={Col} md="6" controlId="first_name">
            {!isPhonePortrait && (
              <Form.Label className="rg-12 gray01">First name</Form.Label>
            )}
            <Form.Control
              required
              type="text"
              placeholder="Enter first name"
              value={state.first_name}
              onChange={onInputChange}
            />
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="email">
            {!isPhonePortrait && (
              <Form.Label className="rg-12 gray01">Email</Form.Label>
            )}
            <Form.Control
              required
              type="email"
              placeholder="Enter email"
              value={state.email}
              onChange={onInputChange}
            />
          </Form.Group>
        </Row>
      ) : (
        <Form.Group controlId="first_name">
          {!isPhonePortrait && (
            <Form.Label className="rg-12 gray01">First name</Form.Label>
          )}
          <Form.Control
            required
            type="text"
            placeholder="Enter first name"
            value={state.first_name}
            onChange={onInputChange}
          />
        </Form.Group>
      )}
      <Form.Group controlId="password">
        {!isPhonePortrait && (
          <Form.Label className="rg-12 gray01">Password</Form.Label>
        )}
        <InputGroup
          className={getClasses(
            "align-items-center mb-2",
            styles["password"],
            isPhonePortrait && styles["password--phone"],
          )}
        >
          <Form.Control
            required
            type={state.revealPassword ? "text" : "password"}
            placeholder="Enter password"
            pattern={PASSWORD_PATTERN.source}
            value={state.password}
            onChange={onInputChange}
            autoComplete="current-password"
          />
          <InputGroup.Text onClick={togglePassword} style={{ cursor: 'pointer' }}>
            {state.revealPassword ? "Hide" : "Show"}
          </InputGroup.Text>
        </InputGroup>
      </Form.Group>
      <div className={`light-gray fs-12 ${state.password ? null : "d-none"}`}>
        <ul
          className={`px-0 ${
            state.showUnmetPasswordRequirement ? "red" : null
          }`}
        >
          <Row>
            <Col md={5}>
              <li
                className={
                  state.password.toLowerCase() !== state.password
                    ? "text-success"
                    : ""
                }
              >
                1 uppercase letter
              </li>
              <li
                className={
                  state.password.toUpperCase() !== state.password
                    ? "text-success"
                    : ""
                }
              >
                1 lowercase letter
              </li>
              <li
                className={
                  /[\^=+$\-*.[\]{}()?"!@#%&\\,><':;|_~`]/.test(state.password)
                    ? "text-success"
                    : ""
                }
              >
                1 special character
              </li>
            </Col>
            <Col md={5}>
              <li className={/\d/.test(state.password) ? "text-success" : ""}>
                1 number
              </li>
              <li className={state.password.length >= 10 ? "text-success" : ""}>
                10 characters or more
              </li>
            </Col>
          </Row>
        </ul>
      </div>
      <Checkbox
        required
        id={isAdultCheckId}
        onChange={onAdultChecked}
        label={
          <label className="fs-14">
            I am over 18 and agree to the{" "}
            <Link to="/terms-of-use" target="_blank">
              Terms of Use
            </Link>{" "}
            and{" "}
            <Link to="/privacy-policy" target="_blank">
              Privacy Policy
            </Link>
          </label>
        }
        className="mb-0"
      />
      {state.error && (
        <Alert variant="danger" className="m-0">
          {state.error}
        </Alert>
      )}
      <Button
        disabled={state.busy}
        variant="primary"
        type="submit"
        className={getClasses(
          isInModal && "w-100",
          "my-3",
          isPhonePortrait && styles["auth-button--phone"],
        )}
      >
        Register{" "}
        {state.busy && (
          <Spinner
            as="span"
            animation="grow"
            size="sm"
            role="status"
            aria-hidden="true"
          />
        )}
      </Button>
    </Form>
  );
};

export default MemberRegisterForm;
